import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTable, useFilters, usePagination } from "react-table";
import { Badge } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { getDefaultParams } from "services/DefaultParamets";
import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import ImageLightbox from "jsx/components/CitoPlus/components/ImageLightbox";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

import "../../../table/FilteringTable/filtering.css";

const VisitantesList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  const columns = useMemo(
    () => [
      {
        id: "foto",
        Header: "Foto",
        accessor: "foto",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) => <ImageLightbox src={value} width={60} />,
      },
      {
        id: "h_ingreso",
        Header: "Fecha Ingreso",
        accessor: "h_ingreso",
        disableFilters: true,
        disableSortable: false,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "nombre",
        Header: "Nombre",
        accessor: "nombre",
        disableFilters: true,
      },
      {
        id: "identificacion",
        Header: "Identificación",
        accessor: "identificacion",
        disableFilters: true,
      },
      {
        id: "autorizado",
        Header: "Autorizado Por",
        accessor: "autorizado",
        disableFilters: true,
      },
      {
        id: "tipo_ingreso_display",
        Header: "Tipo",
        accessor: "tipo_ingreso_display",
        disableFilters: true,
      },
      {
        id: "placa",
        Header: "Placa",
        accessor: "placa",
        disableFilters: true,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "tiene_salida",
        Header: "Estado",
        accessor: "tiene_salida",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) =>
          value ? (
            <Badge bg="success">Salió</Badge>
          ) : (
            <Badge bg="warning">En sitio</Badge>
          ),
      },
    ],
    [],
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedfetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [],
  );

  useEffect(() => {
    fetchUrl(1);
  }, []);

  const fetchUrl = async (pageIndex = 1, search = "", ordering = "") => {
    setLoading(true);
    try {
      const params = {
        ...defaultParams,
        page: pageIndex,
        search: search || searchValue,
        ordering: ordering,
      };

      const response = await ResidenteService.getVisitantes(params);

      setNextUrl(response.data.next);
      setPreviousUrl(response.data.previous);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(response.data.current_page || pageIndex);
      setData(response.data.results || []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedfetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    setSearchValue("");
    fetchUrl(1, "");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";

    setSortField(id);
    setOrder(newOrder);
    setLoading(true);
    fetchUrl(1, searchValue, `${newOrder === "desc" ? "-" : ""}${id}`);
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    usePagination,
  );

  const { page } = tableInstance;

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Mis Visitantes</h4>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-tools">
            <SearchInput
              value={searchValue}
              onSearchChange={handleSearchChange}
              onEnterPress={handleEnterPress}
              onClearClick={handleClearClick}
            />
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <SpinnerCitoPlus text="Cargando visitantes..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-users fa-3x text-muted mb-3"></i>
              <h5>No hay visitantes registrados</h5>
              <p className="text-muted">
                Aquí aparecerán los visitantes que ingresen a tu unidad.
              </p>
            </div>
          ) : (
            <Tables
              columns={columns}
              data={data}
              loading={loading}
              totalPages={totalPages}
              currentPage={currentPage}
              fetchUrl={fetchUrl}
              handleSortingChange={handleSortingChange}
              nextUrl={nextUrl}
              previousUrl={previousUrl}
              sortField={sortField}
              order={order}
              page={page}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default VisitantesList;
